import React from 'react';
import {
  PencilIcon,
  DocumentArrowDownIcon,
  ClockIcon as VersionClockIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  CalendarIcon,
  ClockIcon,
  ScaleIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  LanguageIcon,
  UserIcon,
} from '../../icons';
import JobStatusBadge from './JobStatusBadge';
import TranscriptStatusBadge from './TranscriptStatusBadge';
import CaseReferenceLink from '../cases/CaseReferenceLink';
import { isTranscriptCertified } from '../../utils/transcriptCertification';
import { formatDateTime } from '../../utils/transcriptionMeta';

const ActionMenu = ({ open, onClose, align = 'right', children }) => {
  if (!open) return null;
  return (
    <div
      className={`absolute top-full mt-1 z-50 min-w-[200px] rounded-lg border border-matdash-border bg-white shadow-lg py-1 ${
        align === 'right' ? 'right-0' : 'left-0'
      }`}
      role="menu"
    >
      {children}
      <button type="button" className="sr-only" onClick={onClose} tabIndex={-1} aria-hidden>
        Close
      </button>
    </div>
  );
};

const MenuItem = ({ onClick, disabled, icon: Icon, children }) => (
  <button
    type="button"
    role="menuitem"
    disabled={disabled}
    onMouseDown={(event) => {
      // The page closes menus on document mousedown. Run the action first
      // and stop that closer from unmounting this item before click.
      event.preventDefault();
      event.stopPropagation();
      if (!disabled && onClick) onClick(event);
    }}
    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-matdash-dark hover:bg-matdash-primary-subtle disabled:opacity-50 disabled:cursor-not-allowed text-left"
  >
    {Icon && <Icon className="h-4 w-4 text-matdash-muted shrink-0" />}
    <span>{children}</span>
  </button>
);

const TranscriptionHeader = ({
  transcription,
  error,
  userName,
  isEditDropdownOpen,
  setIsEditDropdownOpen,
  isDownloadDropdownOpen,
  setIsDownloadDropdownOpen,
  isVersionsDropdownOpen,
  setIsVersionsDropdownOpen,
  showVersions,
  showSummaryVersions,
  setShowVersions,
  setShowSummaryVersions,
  versions,
  summaryVersions,
  summary,
  isEditing,
  isEditingSummary,
  loading,
  isLocked = false,
  canCertify = false,
  canRequestRegeneration = false,
  showRegenerateButton = false,
  onGenerateLanguage,
  onCertify,
  onEditTranscripts,
  onEditSummary,
  onDownloadDocx,
  onDownloadPdf,
  onDownloadSummaryDocx,
  onDownloadSummaryPdf,
}) => {
  const isCertified = isTranscriptCertified(transcription);
  const actionsDisabled = loading || Boolean(error);
  const showActions = !isEditing && !isEditingSummary;

  return (
    <div className="md-card mb-6 overflow-visible">
      <div className="md-card-body space-y-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <h1 className="text-xl sm:text-2xl font-bold text-matdash-dark mb-0 break-words">
                {error ? 'No data' : (transcription.title || 'Loading...')}
              </h1>
              {!error && <TranscriptStatusBadge transcription={transcription} />}
            </div>

            {isCertified && transcription.certified_by && (
              <p className="text-sm text-emerald-800 bg-matdash-success-subtle border border-emerald-200 rounded-md px-3 py-2 mb-0 inline-flex items-center gap-1.5">
                <LockClosedIcon className="h-4 w-4 shrink-0" />
                Certified by {transcription.certified_by}
                {transcription.certifying_role ? ` (${transcription.certifying_role})` : ''}
                {transcription.certified_at ? ` on ${formatDateTime(transcription.certified_at)}` : ''}
              </p>
            )}
          </div>

          {showActions && (
            <div className="flex flex-nowrap items-center gap-2 shrink-0 lg:justify-end">
              {canCertify && !isLocked && (
                <button
                  type="button"
                  onClick={onCertify}
                  disabled={actionsDisabled || !transcription.content}
                  className="md-btn md-btn-success md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <ShieldCheckIcon className="h-4 w-4" />
                  Certify
                </button>
              )}
              {showRegenerateButton && canRequestRegeneration && (
                <button
                  type="button"
                  onClick={onGenerateLanguage}
                  disabled={actionsDisabled}
                  className="md-btn md-btn-indigo md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <LanguageIcon className="h-4 w-4" />
                  Regenerate
                </button>
              )}
              {!isLocked && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditDropdownOpen(!isEditDropdownOpen);
                      setIsDownloadDropdownOpen(false);
                      setIsVersionsDropdownOpen(false);
                    }}
                    disabled={actionsDisabled}
                    className="md-btn md-btn-primary md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <PencilIcon className="h-4 w-4" />
                    Edit
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <ActionMenu open={isEditDropdownOpen} onClose={() => setIsEditDropdownOpen(false)}>
                    {/* Transcript editing is disabled while the transcript panel is hidden. */}
                    {/* <MenuItem
                      icon={DocumentTextIcon}
                      onClick={() => {
                        onEditTranscripts();
                        setIsEditDropdownOpen(false);
                      }}
                    >
                      Edit transcript
                    </MenuItem> */}
                    <MenuItem
                      icon={DocumentTextIcon}
                      disabled={!summary}
                      onClick={() => {
                        onEditSummary();
                        setIsEditDropdownOpen(false);
                      }}
                    >
                      Edit summary
                    </MenuItem>
                  </ActionMenu>
                </div>
              )}
              {(showVersions || showSummaryVersions) ? (
                <button
                  type="button"
                  onClick={() => {
                    setShowVersions(false);
                    setShowSummaryVersions(false);
                  }}
                  disabled={actionsDisabled}
                  className="md-btn md-btn-outline md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <VersionClockIcon className="h-4 w-4" />
                  Hide versions
                </button>
              ) : (
                <div className="relative versions-dropdown">
                  <button
                    type="button"
                    onClick={() => {
                      setIsVersionsDropdownOpen(!isVersionsDropdownOpen);
                      setIsEditDropdownOpen(false);
                      setIsDownloadDropdownOpen(false);
                    }}
                    disabled={actionsDisabled}
                    className="md-btn md-btn-outline md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                  >
                    <VersionClockIcon className="h-4 w-4" />
                    Versions
                    <ChevronDownIcon className="h-4 w-4" />
                  </button>
                  <ActionMenu open={isVersionsDropdownOpen} onClose={() => setIsVersionsDropdownOpen(false)}>
                    <MenuItem
                      icon={DocumentTextIcon}
                      onClick={() => {
                        setShowVersions(true);
                        setShowSummaryVersions(false);
                        setIsVersionsDropdownOpen(false);
                      }}
                    >
                      Transcript versions ({versions.length})
                    </MenuItem>
                    {summary && (
                      <MenuItem
                        icon={DocumentTextIcon}
                        onClick={() => {
                          setShowSummaryVersions(true);
                          setShowVersions(false);
                          setIsVersionsDropdownOpen(false);
                        }}
                      >
                        Summary versions ({summaryVersions.length})
                      </MenuItem>
                    )}
                  </ActionMenu>
                </div>
              )}
              <div className="relative download-dropdown" onMouseDown={(event) => event.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => {
                    setIsDownloadDropdownOpen(!isDownloadDropdownOpen);
                    setIsEditDropdownOpen(false);
                    setIsVersionsDropdownOpen(false);
                  }}
                  disabled={actionsDisabled}
                  className="md-btn md-btn-outline md-btn-sm inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  <DocumentArrowDownIcon className="h-4 w-4" />
                  Export
                  <ChevronDownIcon className="h-4 w-4" />
                </button>
                <ActionMenu open={isDownloadDropdownOpen} onClose={() => setIsDownloadDropdownOpen(false)}>
                  {/* Transcript export is intentionally disabled; only summary export is offered. */}
                  {/* <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-matdash-muted">
                    Transcript
                  </div>
                  <MenuItem
                    icon={DocumentArrowDownIcon}
                    onClick={() => {
                      onDownloadDocx();
                      setIsDownloadDropdownOpen(false);
                    }}
                  >
                    Download DOCX
                  </MenuItem>
                  <MenuItem
                    icon={DocumentArrowDownIcon}
                    onClick={() => {
                      onDownloadPdf();
                      setIsDownloadDropdownOpen(false);
                    }}
                  >
                    Download PDF
                  </MenuItem> */}
                  {summary && (
                    <>
                      <div className="px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wide text-matdash-muted">
                        Summary
                      </div>
                      <MenuItem
                        icon={DocumentArrowDownIcon}
                        onClick={() => {
                          onDownloadSummaryDocx();
                          setIsDownloadDropdownOpen(false);
                        }}
                      >
                        Export DOCX
                      </MenuItem>
                      <MenuItem
                        icon={DocumentArrowDownIcon}
                        onClick={() => {
                          onDownloadSummaryPdf();
                          setIsDownloadDropdownOpen(false);
                        }}
                      >
                        Export PDF
                      </MenuItem>
                    </>
                  )}
                </ActionMenu>
              </div>
            </div>
          )}
        </div>

        {!error && (
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-matdash-muted border-t border-matdash-border pt-3">
            {transcription.case_reference && (
              <div className="inline-flex items-center gap-1.5 min-w-0">
                <ScaleIcon className="h-4 w-4 shrink-0" />
                <CaseReferenceLink
                  caseReference={transcription.case_reference}
                  caseId={transcription.case_id}
                />
              </div>
            )}
            <div className="inline-flex items-center gap-1.5">
              <CalendarIcon className="h-4 w-4 shrink-0" />
              <span>{transcription.date || '—'}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <ClockIcon className="h-4 w-4 shrink-0" />
              <span>{transcription.duration || '—'}</span>
            </div>
            <div className="inline-flex items-center gap-1.5">
              <UserIcon className="h-4 w-4 shrink-0" />
              <span>{userName}</span>
            </div>
            <JobStatusBadge status={transcription.status} />
          </div>
        )}

        {error && (
          <div className="md-alert md-alert-danger mb-0">
            <p className="mb-0">{error}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TranscriptionHeader;
